import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import {
  SUBHOME_GALERIA_MAX_ITEMS,
  SUBHOME_GALERIA_MIN_ITEMS,
  SUBHOME_GALERIA_TITLE,
} from './helpers';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_subhome_galeria_block',
    defaultMessage: 'Carrossel de imagens (sub-home)',
  },
  title: { id: 'mpf_subhome_galeria_title', defaultMessage: 'Título da faixa' },
  titleHelp: {
    id: 'mpf_subhome_galeria_title_help',
    defaultMessage: 'Em branco, usa "Carrossel Imagens".',
  },
  items: { id: 'mpf_subhome_galeria_items', defaultMessage: 'Imagens' },
  itemsHelp: {
    id: 'mpf_subhome_galeria_items_help',
    defaultMessage:
      'De {min} a {max} imagens. Item sem imagem não aparece na faixa.',
  },
  item: { id: 'mpf_subhome_galeria_item', defaultMessage: 'Imagem' },
  image: { id: 'mpf_subhome_galeria_image', defaultMessage: 'Imagem' },
  imageHelp: {
    id: 'mpf_subhome_galeria_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL.',
  },
  alt: {
    id: 'mpf_subhome_galeria_alt',
    defaultMessage: 'Texto alternativo',
  },
  altHelp: {
    id: 'mpf_subhome_galeria_alt_help',
    defaultMessage: 'Descreva o que a imagem mostra, para leitores de tela.',
  },
  caption: { id: 'mpf_subhome_galeria_caption', defaultMessage: 'Legenda' },
  captionHelp: {
    id: 'mpf_subhome_galeria_caption_help',
    defaultMessage:
      'Texto abaixo da imagem. Em branco, o card fica só com a foto.',
  },
  href: { id: 'mpf_subhome_galeria_href', defaultMessage: 'Destino' },
  hrefHelp: {
    id: 'mpf_subhome_galeria_href_help',
    defaultMessage:
      'Página do site ou endereço externo. Com destino preenchido, o card inteiro vira link.',
  },
  external: {
    id: 'mpf_subhome_galeria_external',
    defaultMessage: 'Abrir em nova aba',
  },
});

/** Campos de uma imagem da galeria. */
const itemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['image', 'alt', 'caption', 'href', 'external'],
    },
  ],
  properties: {
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL.
    image: {
      title: intl.formatMessage(messages.image),
      description: intl.formatMessage(messages.imageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.alt),
      description: intl.formatMessage(messages.altHelp),
    },
    caption: {
      title: intl.formatMessage(messages.caption),
      description: intl.formatMessage(messages.captionHelp),
    },
    href: {
      title: intl.formatMessage(messages.href),
      description: intl.formatMessage(messages.hrefHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.external),
      type: 'boolean',
    },
  },
  required: ['image', 'alt'],
});

/**
 * Schema do bloco "Carrossel de imagens (sub-home)".
 *
 * `activeObject`/`setActiveObject` mantêm o `ObjectListWidget` no modo
 * controlado: `-1` (e não `undefined`) é o que o deixa com tudo fechado — ver
 * `Blocks/Destaques/schema.ts`.
 */
export const mpfSubHomeGaleriaSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [{ id: 'default', title: 'Default', fields: ['title', 'items'] }],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: SUBHOME_GALERIA_TITLE,
    },
    items: {
      title: intl.formatMessage(messages.items),
      description: intl.formatMessage(messages.itemsHelp, {
        min: SUBHOME_GALERIA_MIN_ITEMS,
        max: SUBHOME_GALERIA_MAX_ITEMS,
      }),
      widget: 'object_list',
      schema: itemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfSubHomeGaleriaSchema;
